import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { Button, SHAPE } from "baseui/button";
import { Checkbox, STYLE_TYPE } from "baseui/checkbox";
import { Check, Delete } from "baseui/icon";
import { DURATION, useSnackbar } from "baseui/snackbar";
import { Textarea } from "baseui/textarea";
import { HeadingLarge, LabelMedium, LabelSmall, ParagraphSmall } from "baseui/typography";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getAdminChats, getAdminRecipients, getAdminSettings, getAdminSolutions, sendAdminMessage, setSolutionApproval, updateAdminSettings } from "../application/actions/admin";
import { routeTo } from "../application/actions/ui";
import ReDoButton from "./ReDoButton";
import ReDoInput from "./ReDoInput";
import ReDoMenuCard from "./ReDoMenuCard";
import ReDoSpinner from "./ReDoSpinner";

const AUDIENCES = [
  { key: 'all_users', label: 'All Users' },
  { key: 'all_companies', label: 'All Companies' },
  { key: 'selected', label: 'Selected' },
];

const SECTIONS = [
  { key: 'chats', label: 'Admin Chats' },
  { key: 'broadcast', label: 'Broadcast' },
  { key: 'approvals', label: 'Approvals' },
];

const CHAT_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
];

// The ReDo admin screen: a shared "Admin Chats" inbox (every admin sees and can
// reply to every admin<->user conversation, since they all go through one
// shared system identity), the broadcast composer, and the moderation queue.
// Rendered both as the standalone /admin view and as an admin-only tab inside
// the normal profile.
const ReDoAdminPanel = () => {
  const dispatch = useDispatch();
  const [, theme] = useStyletron();
  const { enqueue } = useSnackbar();

  const [section, setSection] = useState('chats');

  // ---- admin chats: shared across every admin ----
  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  const [chatFilter, setChatFilter] = useState('all');
  const chatsRequestId = useRef(0);

  const visibleChats = chatFilter === 'all' ? chats
    : chatFilter === 'unread' ? chats.filter(c => !!c.unread)
    : chats.filter(c => !c.unread);

  const fetchChats = useCallback(() => {
    // stale-response guard: if fetchChats is called again (e.g. right after a
    // broadcast send) before this request resolves, only the latest one may
    // commit its result
    const requestId = ++chatsRequestId.current;
    setLoadingChats(true);
    dispatch(getAdminChats)
      .then(r => { if (requestId === chatsRequestId.current) setChats(Array.isArray(r) ? r : []); })
      .catch(() => { if (requestId === chatsRequestId.current) enqueue({ message: 'Could not load admin chats', dismissAfter: DURATION.short, startEnhancer: () => <Delete size={theme.sizing.scale800} /> }); })
      .finally(() => { if (requestId === chatsRequestId.current) setLoadingChats(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  const onChatClick = (ev, item) => {
    ev.stopPropagation();
    dispatch(routeTo({ view: 'chat', payload: { item } }));
  };

  const [audience, setAudience] = useState('all_users');
  const [content, setContent] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (audience === 'selected' && recipients.length === 0) {
      setLoadingRecipients(true);
      dispatch(getAdminRecipients)
        .then(r => setRecipients(Array.isArray(r) ? r : []))
        .catch(() => enqueue({ message: 'Could not load recipients', dismissAfter: DURATION.short, startEnhancer: () => <Delete size={theme.sizing.scale800} /> }))
        .finally(() => setLoadingRecipients(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience]);

  const toggle = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const canSend = content.trim().length > 0 && (audience !== 'selected' || selectedIds.length > 0) && !sending;

  const onSend = () => {
    if (!canSend) return;
    setSending(true);
    dispatch(sendAdminMessage({
      audience,
      recipientIds: audience === 'selected' ? selectedIds : undefined,
      content: content.trim(),
    }))
      .then(res => {
        enqueue({ message: `Message sent to ${res?.sent ?? 0} recipient(s)`, dismissAfter: DURATION.short, startEnhancer: () => <Check size={theme.sizing.scale800} /> });
        setContent('');
        setSelectedIds([]);
        // the newly created/updated thread(s) should show up in Admin Chats immediately
        fetchChats();
      })
      .catch(e => enqueue({ message: e?.response?.data?.error || e?.message || 'Could not send message', dismissAfter: DURATION.short, startEnhancer: () => <Delete size={theme.sizing.scale800} /> }))
      .finally(() => setSending(false));
  };

  const filtered = recipients.filter(r => (r.name || '').toLowerCase().includes(search.toLowerCase()));

  // ---- moderation: global switches + approval queue ----
  const [settings, setSettings] = useState(null);
  const [settingsError, setSettingsError] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [solutions, setSolutions] = useState([]);
  const [loadingSolutions, setLoadingSolutions] = useState(false);
  const [onlyPending, setOnlyPending] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const toastError = useCallback((message) => enqueue({ message, dismissAfter: DURATION.short, startEnhancer: () => <Delete size={theme.sizing.scale800} /> }), [enqueue, theme]);

  const fetchSettings = () => {
    setSettingsError(false);
    dispatch(getAdminSettings)
      .then(s => setSettings(s || {}))
      .catch(() => { setSettingsError(true); toastError('Could not load moderation settings'); });
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // ignore-flag guard: switching the filter twice quickly must not let a slow
    // earlier response overwrite the list with data for the wrong filter
    let ignore = false;
    setLoadingSolutions(true);
    dispatch(getAdminSolutions(onlyPending))
      .then(r => { if (!ignore) setSolutions(Array.isArray(r) ? r : []); })
      .catch(() => { if (!ignore) toastError('Could not load solutions'); })
      .finally(() => { if (!ignore) setLoadingSolutions(false); });
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyPending]);

  const onToggleSetting = (key) => {
    if (savingSettings) return;
    const next = {
      requireApprovalFreeSolutions: !!settings?.requireApprovalFreeSolutions,
      requireApprovalChallengeSolutions: !!settings?.requireApprovalChallengeSolutions,
    };
    next[key] = !next[key];
    setSavingSettings(true);
    dispatch(updateAdminSettings(next))
      .then(s => setSettings(s || next))
      .catch(() => toastError('Could not save moderation settings'))
      .finally(() => setSavingSettings(false));
  };

  const onToggleApproval = (item) => {
    if (updatingId) return;
    setUpdatingId(item.id);
    const nextApproved = !item.approved;
    dispatch(setSolutionApproval(item.id, nextApproved))
      .then(updated => {
        const approved = updated?.approved ?? nextApproved;
        setSolutions(prev => onlyPending && approved
          // it is no longer pending, so drop it from the pending list rather than
          // leaving an "approved" row under the "Pending approval" heading
          ? prev.filter(s => s.id !== item.id)
          : prev.map(s => s.id === item.id ? { ...s, approved } : s));
        enqueue({ message: approved ? 'Solution approved' : 'Approval revoked', dismissAfter: DURATION.short, startEnhancer: () => <Check size={theme.sizing.scale800} /> });
      })
      .catch(() => toastError('Could not update approval'))
      .finally(() => setUpdatingId(null));
  };

  return (
    <Block overrides={{ Block: { style: { maxWidth: '640px', margin: '0 auto' } } }} padding='2ch 0 10ch 0'>
      {sending && <ReDoSpinner />}

      <HeadingLarge>ReDo Admin</HeadingLarge>

      <Block display='flex' gridGap='1ch' flexWrap='wrap' marginTop='2ch' marginBottom='3ch'>
        {SECTIONS.map(s => (
          <Button
            key={s.key}
            onClick={() => setSection(s.key)}
            aria-pressed={section === s.key}
            shape={SHAPE.pill}
            kind="tertiary"
            overrides={{ BaseButton: { style: {
              backgroundColor: section === s.key ? theme.colors.black : 'white',
              color: section === s.key ? 'white' : 'black',
              boxShadow: `0 0 15px 0px rgba(94, 94, 91, 20%)`,
              ':hover': { backgroundColor: section === s.key ? theme.colors.black : theme.colors.secondary200 },
            } } }}
          >
            {s.label}
          </Button>
        ))}
      </Block>

      {section === 'chats' && <>
        <Block display='flex' gridGap='1ch' flexWrap='wrap' marginBottom='2ch'>
          {CHAT_FILTERS.map(f => (
            <Button
              key={f.key}
              onClick={() => setChatFilter(f.key)}
              aria-pressed={chatFilter === f.key}
              shape={SHAPE.pill}
              kind="tertiary"
              overrides={{ BaseButton: { style: {
                backgroundColor: chatFilter === f.key ? theme.colors.black : 'white',
                color: chatFilter === f.key ? 'white' : 'black',
                boxShadow: `0 0 15px 0px rgba(94, 94, 91, 20%)`,
                ':hover': { backgroundColor: chatFilter === f.key ? theme.colors.black : theme.colors.secondary200 },
              } } }}
            >
              {f.label}
            </Button>
          ))}
        </Block>

        <Block maxHeight='480px' overflow='auto'>
          {loadingChats
            ? <ParagraphSmall color={theme.colors.primary200}>Loading chats…</ParagraphSmall>
            : visibleChats.length === 0 && chats.length > 0
              // ReDoMenuCard's own empty state ("Nothing to show yet") would be
              // misleading here - there are chats, just none matching the filter
              ? <ParagraphSmall color={theme.colors.primary200}>No {chatFilter} chats.</ParagraphSmall>
              : <ReDoMenuCard onClick={onChatClick} options={visibleChats} />
          }
        </Block>
      </>}

      {section === 'broadcast' && <>
      <LabelMedium marginTop='0' marginBottom='1ch'>Send a message</LabelMedium>

      <Block display='flex' gridGap='1ch' flexWrap='wrap' marginBottom='2ch'>
        {AUDIENCES.map(a => (
          <Button
            key={a.key}
            onClick={() => setAudience(a.key)}
            disabled={sending}
            aria-pressed={audience === a.key}
            shape={SHAPE.pill}
            kind="tertiary"
            overrides={{ BaseButton: { style: {
              backgroundColor: audience === a.key ? theme.colors.black : 'white',
              color: audience === a.key ? 'white' : 'black',
              boxShadow: `0 0 15px 0px rgba(94, 94, 91, 20%)`,
              ':hover': { backgroundColor: audience === a.key ? theme.colors.black : theme.colors.secondary200 },
            } } }}
          >
            {a.label}
          </Button>
        ))}
      </Block>

      {audience === 'selected' &&
        <Block marginBottom='2ch'>
          <ReDoInput placeholder='Search users & companies' value={search} onChange={e => setSearch(e.target.value)} shadow />
          <LabelSmall marginTop='1ch' color={theme.colors.primary200}>{selectedIds.length} selected</LabelSmall>
          {loadingRecipients
            ? <ParagraphSmall color={theme.colors.primary200}>Loading recipients…</ParagraphSmall>
            : <Block marginTop='1ch' maxHeight='260px' overflow='auto' display='flex' flexDirection='column' gridGap='0.5ch'>
                {filtered.map(r => (
                  <Block
                    key={r.userId}
                    onClick={() => toggle(r.userId)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(r.userId); } }}
                    role='checkbox'
                    tabIndex={0}
                    aria-checked={selectedIds.includes(r.userId)}
                    aria-label={`${r.name || 'Unnamed'} (${r.type})`}
                    display='flex'
                    alignItems='center'
                    justifyContent='space-between'
                    padding='0.75ch 1ch'
                    overrides={{ Block: { style: { cursor: 'pointer', borderRadius: '8px', backgroundColor: selectedIds.includes(r.userId) ? theme.colors.secondary0 : 'white', ':focus': { outline: `2px solid ${theme.colors.primary200}` } } } }}
                  >
                    <Block display='flex' flexDirection='column'>
                      <LabelSmall>{r.name || 'Unnamed'}</LabelSmall>
                      <ParagraphSmall margin='0' color={theme.colors.primary200}>{r.type}</ParagraphSmall>
                    </Block>
                    {selectedIds.includes(r.userId) && <Check size={20} color={theme.colors.positive} />}
                  </Block>
                ))}
                {filtered.length === 0 && <ParagraphSmall color={theme.colors.primary200}>No matches.</ParagraphSmall>}
              </Block>
          }
        </Block>
      }

      <Textarea
        value={content}
        onChange={e => setContent(e.target.value)}
        placeholder='Type your message…'
        overrides={{
          Root: { style: { boxShadow: `0 0 15px 0px rgba(94, 94, 91, 20%)`, border: '0' } },
          Input: { style: { minHeight: '120px' } },
        }}
      />

      <Block width={['100%', '100%', '240px', '240px']} marginTop='2ch'>
        <ReDoButton kind='brand' disabled={!canSend} onClick={onSend}>Send message</ReDoButton>
      </Block>
      </>}

      {section === 'approvals' && <>
      <Block marginTop='0' paddingTop='0'>
        <LabelMedium marginBottom='1ch'>Approvals</LabelMedium>
        <ParagraphSmall marginTop='0' color={theme.colors.primary200}>
          While a switch is on, newly submitted solutions stay hidden until you approve them. Turning it off shows everything again.
        </ParagraphSmall>

        {settingsError
          ? <Block display='flex' alignItems='center' gridGap='1ch' marginTop='1ch'>
              <ParagraphSmall margin='0' color={theme.colors.primary200}>Could not load settings.</ParagraphSmall>
              <Button kind='tertiary' onClick={fetchSettings} overrides={{ BaseButton: { style: { backgroundColor: 'white', boxShadow: `0 0 15px 0px rgba(94, 94, 91, 20%)` } } }}>Retry</Button>
            </Block>
          : settings === null
            ? <ParagraphSmall color={theme.colors.primary200}>Loading settings…</ParagraphSmall>
            : <>
                <Checkbox
                  checked={!!settings.requireApprovalFreeSolutions}
                  disabled={savingSettings}
                  onChange={() => onToggleSetting('requireApprovalFreeSolutions')}
                  checkmarkType={STYLE_TYPE.toggle_round}
                >
                  Require approval for solutions
                </Checkbox>

                <Block marginTop='1ch'>
                  <Checkbox
                    checked={!!settings.requireApprovalChallengeSolutions}
                    disabled={savingSettings}
                    onChange={() => onToggleSetting('requireApprovalChallengeSolutions')}
                    checkmarkType={STYLE_TYPE.toggle_round}
                  >
                    Require approval for challenge submissions
                  </Checkbox>
                </Block>
              </>
        }
      </Block>

      <Block marginTop='3ch'>
        <Block display='flex' alignItems='center' justifyContent='space-between' marginBottom='1ch'>
          <LabelMedium>{onlyPending ? 'Pending approval' : 'All solutions'}</LabelMedium>
          <Button
            onClick={() => setOnlyPending(p => !p)}
            aria-pressed={onlyPending}
            shape={SHAPE.pill}
            kind="tertiary"
            overrides={{ BaseButton: { style: { backgroundColor: 'white', boxShadow: `0 0 15px 0px rgba(94, 94, 91, 20%)` } } }}
          >
            {onlyPending ? 'Show all' : 'Show pending'}
          </Button>
        </Block>

        {loadingSolutions
          ? <ParagraphSmall color={theme.colors.primary200}>Loading solutions…</ParagraphSmall>
          : <Block display='flex' flexDirection='column' gridGap='0.5ch' maxHeight='360px' overflow='auto'>
              {solutions.map(item => (
                <Block
                  key={item.id}
                  display='flex'
                  alignItems='center'
                  justifyContent='space-between'
                  gridGap='1ch'
                  padding='0.75ch 1ch'
                  overrides={{ Block: { style: { borderRadius: '8px', backgroundColor: 'white' } } }}
                >
                  <Block display='flex' flexDirection='column' overrides={{ Block: { style: { minWidth: '0' } } }}>
                    <LabelSmall overrides={{ Block: { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } } }}>{item.title}</LabelSmall>
                    <ParagraphSmall margin='0' color={theme.colors.primary200}>
                      {item.challengeID ? 'challenge submission' : 'solution'} · {item.approved ? 'approved' : 'pending'}
                    </ParagraphSmall>
                  </Block>
                  <Block overrides={{ Block: { style: { flexShrink: '0' } } }}>
                    <Button
                      onClick={() => onToggleApproval(item)}
                      disabled={!!updatingId}
                      aria-label={`${item.approved ? 'Revoke approval for' : 'Approve'} ${item.title}`}
                      shape={SHAPE.pill}
                      kind="tertiary"
                      overrides={{ BaseButton: { style: {
                        backgroundColor: item.approved ? 'white' : theme.colors.black,
                        color: item.approved ? 'black' : 'white',
                        boxShadow: `0 0 15px 0px rgba(94, 94, 91, 20%)`,
                      } } }}
                    >
                      {item.approved ? 'Revoke' : 'Approve'}
                    </Button>
                  </Block>
                </Block>
              ))}
              {solutions.length === 0 && <ParagraphSmall color={theme.colors.primary200}>{onlyPending ? 'Nothing waiting for approval.' : 'No solutions yet.'}</ParagraphSmall>}
            </Block>
        }
      </Block>
      </>}
    </Block>
  );
};

export default ReDoAdminPanel;
